import React, { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

import { GridTypeahead } from '@/components/common/grid-typeahead';
import { LineItemsTable, type LineItemColumn } from '@/components/common/line-items-table';
import { Button, Modal, Text } from '@/components/ui';
import type { StandardGl } from '@/utils/gl-standard-match';
import { type GlCodeMapping, type GlCodeResolution, glMappingMatchKind } from '@/utils/qb-compile';

export interface GlCodeMappingModalProps {
	visible: boolean;
	/** Every distinct GL code referenced by the journal, with its resolved target. */
	mappings: GlCodeMapping[];
	/** The standard chart to map onto. */
	standardDefaults: StandardGl[];
	/** Confirmed decisions, one per incoming code. */
	onResolve: (decisions: GlCodeResolution[]) => void;
	onCancel: () => void;
}

/** Sentinel id for the synthetic "create this code as new" option. */
const CREATE_ID = '__create__';

const COLUMNS: LineItemColumn[] = [
	{ key: 'incoming', label: 'QuickBooks code', width: '40%' },
	{ key: 'lines', label: 'Lines', width: '12%', align: 'right' },
	{ key: 'target', label: 'Maps to (standard chart)', width: '48%' },
];

/**
 * Full-screen spreadsheet for reviewing how each QuickBooks GL code maps onto the
 * standard chart BEFORE the import commits. One row per incoming code; the target
 * cell is a typeahead over the standard chart with a pinned "NEW — create this
 * code" option. Defaults to the resolved standard mapping, so confirming with no
 * changes keeps every client on the shared chart.
 */
export const GlCodeMappingModal: React.FC<GlCodeMappingModalProps> = ({
	visible, mappings, standardDefaults, onResolve, onCancel,
}) => {
	const [decisions, setDecisions] = useState<Record<string, GlCodeResolution>>(() => {
		const init: Record<string, GlCodeResolution> = {};
		for (const m of mappings) init[m.incomingCode] = { incomingCode: m.incomingCode, glId: m.resolvedGlId };
		return init;
	});

	const setGlId = (incomingCode: string, glId: string | null): void =>
		setDecisions((d) => ({ ...d, [incomingCode]: { incomingCode, glId } }));

	const standardById = React.useMemo(() => new Map(standardDefaults.map((g) => [g.id, g])), [standardDefaults]);
	// Classify against the LIVE decision so highlighting updates as the user picks.
	const kindOf = (line: GlCodeMapping) => glMappingMatchKind(decisions[line.incomingCode]?.glId ?? null, line.incomingCode, standardById);

	const createCount = mappings.filter((m) => !decisions[m.incomingCode]?.glId).length;
	const suggestedCount = mappings.filter((m) => kindOf(m) === 'suggested').length;
	const rowStyle = ({ line }: { line: GlCodeMapping }) => {
		const k = kindOf(line);
		return k === 'new' ? styles.rowNew : k === 'suggested' ? styles.rowSuggested : undefined;
	};

	const toOption = (g: StandardGl) => (g.id === CREATE_ID
		? { id: CREATE_ID, display: `NEW · ${g.name}`, primary: 'NEW', secondary: g.name }
		: { id: g.id, display: `${g.code} · ${g.name}`, primary: g.code, secondary: g.name, tertiary: g.accountType });

	const renderCell = ({ line, column }: { line: GlCodeMapping; column: LineItemColumn }): React.ReactNode => {
		const dec = decisions[line.incomingCode];
		if (column.key === 'incoming') {
			return (
				<Text style={styles.incoming} numberOfLines={1}>
					{line.incomingCode}{line.incomingName ? ` · ${line.incomingName}` : ''}
				</Text>
			);
		}
		if (column.key === 'lines') {
			return <Text style={styles.lines}>{line.refCount}</Text>;
		}
		// target: typeahead over [NEW, ...standard chart]
		const createItem: StandardGl = { id: CREATE_ID, code: 'NEW', name: `create ${line.incomingCode}`, accountType: '' };
		return (
			<GridTypeahead<StandardGl>
				items={[createItem, ...standardDefaults]}
				toOption={toOption}
				value={dec?.glId ?? CREATE_ID}
				onSelect={(g) => setGlId(line.incomingCode, !g || g.id === CREATE_ID ? null : g.id)}
				placeholder="Search the standard chart…"
				showAllOnEmpty
				pinnedFirst
				testID={`glm-target-${line.incomingCode}`}
			/>
		);
	};

	return (
		<Modal visible={visible} onDismiss={onCancel} contentContainerStyle={styles.modal}>
			<View style={styles.header}>
				<Text style={styles.title}>Review GL code mapping</Text>
				<Text style={styles.subtitle}>
					{mappings.length} GL {mappings.length === 1 ? 'code' : 'codes'} from your QuickBooks journal.
					Each maps onto the standard chart — change any that landed wrong, or pick NEW to create it.
					{createCount > 0 ? ` ${createCount} will be created as new.` : ''}
				</Text>
				<View style={styles.legend}>
					<View style={styles.legendItem}><View style={[styles.swatch, styles.rowSuggested]} /><Text style={styles.legendText}>Not an exact match — review ({suggestedCount})</Text></View>
					<View style={styles.legendItem}><View style={[styles.swatch, styles.rowNew]} /><Text style={styles.legendText}>New record — will be created ({createCount})</Text></View>
				</View>
			</View>

			<ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
				<LineItemsTable<GlCodeMapping> columns={COLUMNS} lines={mappings} renderCell={renderCell} rowStyle={rowStyle} hoverable />
			</ScrollView>

			<View style={styles.footer}>
				<Button mode="text" onPress={onCancel}>Cancel</Button>
				<Button mode="contained" onPress={() => onResolve(mappings.map((m) => decisions[m.incomingCode]))} testID="glm-confirm">
					Confirm &amp; import
				</Button>
			</View>
		</Modal>
	);
};

const styles = StyleSheet.create({
	modal: { width: '96%', maxWidth: 1000, height: '92%', padding: 0 },
	header: { paddingHorizontal: 20, paddingTop: 20, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: '#eceff1' },
	title: { fontSize: 18, fontWeight: '700', color: '#212121', marginBottom: 4 },
	subtitle: { fontSize: 13, color: '#78909c' },
	body: { flex: 1 },
	bodyContent: { padding: 16 },
	incoming: { fontSize: 13, color: '#212121', paddingHorizontal: 8 },
	lines: { fontSize: 13, color: '#546e7a', paddingHorizontal: 8, textAlign: 'right' },
	// Row highlights: amber = fuzzy/renamed target (review), blue = will create new.
	rowSuggested: { backgroundColor: '#fff8e1' },
	rowNew: { backgroundColor: '#e3f2fd' },
	legend: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 10 },
	legendItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
	swatch: { width: 14, height: 14, borderRadius: 3, borderWidth: 1, borderColor: '#cfd8dc' },
	legendText: { fontSize: 12, color: '#546e7a' },
	footer: {
		flexDirection: 'row', justifyContent: 'flex-end', gap: 8,
		paddingHorizontal: 20, paddingVertical: 14, borderTopWidth: 1, borderTopColor: '#eceff1',
	},
});
